import { useState, useEffect, useCallback, useMemo, type ReactNode, type ChangeEvent } from "react";
import PageHeader from "../components/PageHeader";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Input,
    SelectBox,
    type SelectOption,
} from "../components/atoms";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
    CommonTableEditHeaderCell,
} from "../components/table";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";
import {
    fetchAccounts,
    fetchAccountRoles,
    createAccount,
    updateAccount,
    resetAccountPassword,
    type AccountResponse,
    type AccountRoleResponse,
} from "../api/accounts";

// ─────────────────────────────────────────────
// 定数・ヘルパー
// ─────────────────────────────────────────────

const EMPTY = "—";
const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";

const toKey = (val: string | number | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : String(val);

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

const PASSWORD_STATUS_LABEL: Record<NonNullable<AccountResponse["password_status"]>, string> = {
    normal: "正常",
    locked: "ロック",
    initial: "初期パスワード",
};

const passwordStatusToKey = (status: AccountResponse["password_status"]): string =>
    status ?? EMPTY_VALUE_KEY;

const passwordStatusLabel = (key: string): string => {
    if (key === EMPTY_VALUE_KEY) {return EMPTY_VALUE_LABEL;}
    if (key in PASSWORD_STATUS_LABEL) {
        return PASSWORD_STATUS_LABEL[key as keyof typeof PASSWORD_STATUS_LABEL];
    }
    return key;
};

const formatPasswordStatus = (status: AccountResponse["password_status"]): string =>
    status ? PASSWORD_STATUS_LABEL[status] : EMPTY;

type SortColumn = "accountName" | "accountCode" | "role" | "passwordStatus";

// ─────────────────────────────────────────────
// フォーム型・初期値
// ─────────────────────────────────────────────

interface AccountFormValues {
    account_name: string;
    account_code: string;
    account_role_id: string;
}

const EMPTY_FORM: AccountFormValues = {
    account_name: "",
    account_code: "",
    account_role_id: "",
};

// ─────────────────────────────────────────────
// ダイアログ内フォーム行
// ─────────────────────────────────────────────

interface FormRowProps {
    label: string;
    children: ReactNode;
}

const FormRow = ({ label, children }: FormRowProps) => (
    <div className="flex items-center gap-6">
        <span className="w-32 shrink-0 text-sm font-medium text-gray-700">{label}</span>
        <div className="flex-1">{children}</div>
    </div>
);

// ─────────────────────────────────────────────
// パスワード初期化確認ダイアログ
// ─────────────────────────────────────────────

interface PasswordResetDialogProps {
    account: AccountResponse;
    onSuccess: () => void;
    onCancel: () => void;
}

const PasswordResetDialog = ({ account, onSuccess, onCancel }: PasswordResetDialogProps) => {
    const [isResetting, setIsResetting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleConfirm = async () => {
        setIsResetting(true);
        setErrorMessage(null);
        try {
            await resetAccountPassword(account.id);
            onSuccess();
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : "パスワードの初期化に失敗しました");
        } finally {
            setIsResetting(false);
        }
    };

    const handleCancel = () => {
        if (isResetting) {return;}
        onCancel();
    };

    return (
        <Dialog onClose={isResetting ? undefined : handleCancel} className="w-[440px]">
            <DialogHeader
                title="パスワード初期化"
                onClose={isResetting ? undefined : handleCancel}
                className="border-b-0"
            />
            <DialogBody>
                <p className="text-sm text-gray-700">
                    <span className="font-medium">{account.account_name}</span> さんのパスワードを初期化します。よろしいですか？
                </p>
                {errorMessage && (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                )}
            </DialogBody>
            <DialogFooter className="border-t-0">
                <Button variant="outline" size="md" onClick={handleCancel} disabled={isResetting}>
                    キャンセル
                </Button>
                <Button variant="primary" size="md" onClick={handleConfirm} disabled={isResetting}>
                    {isResetting ? "初期化中..." : "初期化する"}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

// ─────────────────────────────────────────────
// アカウント登録/変更ダイアログ
// ─────────────────────────────────────────────

interface AccountFormDialogProps {
    mode: "create" | "edit";
    initialValues: AccountFormValues;
    roleOptions: SelectOption[];
    onSubmit: (values: AccountFormValues) => Promise<void>;
    onCancel: () => void;
}

const AccountFormDialog = ({
    mode,
    initialValues,
    roleOptions,
    onSubmit,
    onCancel,
}: AccountFormDialogProps) => {
    const [values, setValues] = useState<AccountFormValues>(initialValues);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const title = mode === "create" ? "新規登録" : "変更";

    const handleChange = (field: keyof AccountFormValues) => (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        setValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async () => {
        if (!values.account_name.trim() || !values.account_code.trim() || !values.account_role_id) {
            setErrorMessage("すべての項目を入力してください");
            return;
        }
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            await onSubmit(values);
        } catch (e) {
            setErrorMessage(e instanceof Error ? e.message : "保存に失敗しました");
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (isSubmitting) {return;}
        onCancel();
    };

    return (
        <Dialog
            onClose={isSubmitting ? undefined : handleCancel}
            className="w-[600px] border border-brand-500"
        >
            <DialogHeader
                title={title}
                onClose={isSubmitting ? undefined : handleCancel}
            />
            <DialogBody className="flex flex-col gap-6 py-6">
                <FormRow label="氏名">
                    <Input
                        placeholder="氏名を入力してください"
                        value={values.account_name}
                        onChange={handleChange("account_name")}
                        disabled={isSubmitting}
                    />
                </FormRow>
                <FormRow label="アカウントコード">
                    <Input
                        placeholder="アカウントコードを入力してください"
                        value={values.account_code}
                        onChange={handleChange("account_code")}
                        disabled={isSubmitting}
                    />
                </FormRow>
                <FormRow label="権限">
                    <SelectBox
                        options={roleOptions}
                        value={values.account_role_id}
                        onChange={handleChange("account_role_id")}
                        disabled={isSubmitting}
                        placeholder="選択してください"
                        size="md"
                    />
                </FormRow>
                {errorMessage && (
                    <p className="text-sm text-red-600">{errorMessage}</p>
                )}
            </DialogBody>
            <DialogFooter>
                <Button
                    variant="outline"
                    size="md"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    キャンセル
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                >
                    {mode === "create" ? "登録" : "変更"}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

// ─────────────────────────────────────────────
// メインページ
// ─────────────────────────────────────────────

type DialogState =
    | { type: "closed" }
    | { type: "create" }
    | { type: "edit"; account: AccountResponse };

const AccountPage = () => {
    const [accounts, setAccounts] = useState<AccountResponse[]>([]);
    const [roles, setRoles] = useState<AccountRoleResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialog, setDialog] = useState<DialogState>({ type: "closed" });
    const [resetTarget, setResetTarget] = useState<AccountResponse | null>(null);

    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const roleOptions: SelectOption[] = useMemo(
        () => roles.map((r) => ({ value: String(r.id), label: r.name })),
        [roles],
    );

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [accountList, roleList] = await Promise.all([
                fetchAccounts(),
                fetchAccountRoles(),
            ]);
            setAccounts(accountList);
            setRoles(roleList);
        } catch (e) {
            setError(e instanceof Error ? e.message : "データの取得に失敗しました");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ── 列ごとのフィルター ──
    const accountNameFilter = useColumnFilter(accounts, (a) => toKey(a.account_name), labelOrEmpty);
    const accountCodeFilter = useColumnFilter(accounts, (a) => toKey(a.account_code), labelOrEmpty);
    const roleFilter = useColumnFilter(
        accounts,
        (a) => toKey(a.permission_level),
        labelOrEmpty,
    );
    const passwordStatusFilter = useColumnFilter(
        accounts,
        (a) => passwordStatusToKey(a.password_status),
        passwordStatusLabel,
    );

    const filterColumnList = useMemo(
        () => [
            { column: "accountName" as SortColumn, label: "氏名", widthClass: "min-w-[180px]", filter: accountNameFilter },
            { column: "accountCode" as SortColumn, label: "アカウントコード", widthClass: "min-w-[160px]", filter: accountCodeFilter },
            { column: "role" as SortColumn, label: "権限", widthClass: "min-w-[80px]", filter: roleFilter },
            { column: "passwordStatus" as SortColumn, label: "パスワード状態", widthClass: "min-w-[140px]", filter: passwordStatusFilter },
        ],
        [accountNameFilter, accountCodeFilter, roleFilter, passwordStatusFilter],
    );

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const target = filterColumnList.find((c) => c.column === which);
            if (!target) {return;}
            target.filter.anchorRef.current = anchor;
            const willOpen = !target.filter.open;
            target.filter.setOpen(willOpen);
            if (willOpen) {
                filterColumnList.forEach((c) => {
                    if (c.column !== which) {c.filter.setOpen(false);}
                });
            }
        },
        [filterColumnList],
    );

    // ── フィルター・ソート適用後の一覧 ──
    const displayedAccounts = useMemo(() => {
        let list = accounts;

        if (accountNameFilter.isFiltering) {
            const set = new Set(accountNameFilter.selected);
            list = list.filter((a) => set.has(toKey(a.account_name)));
        }
        if (accountCodeFilter.isFiltering) {
            const set = new Set(accountCodeFilter.selected);
            list = list.filter((a) => set.has(toKey(a.account_code)));
        }
        if (roleFilter.isFiltering) {
            const set = new Set(roleFilter.selected);
            list = list.filter((a) => set.has(toKey(a.permission_level)));
        }
        if (passwordStatusFilter.isFiltering) {
            const set = new Set(passwordStatusFilter.selected);
            list = list.filter((a) => set.has(passwordStatusToKey(a.password_status)));
        }

        if (activeSort) {
            const cmp = (a: AccountResponse, b: AccountResponse): number => {
                switch (activeSort.column) {
                    case "accountName":
                        return a.account_name.localeCompare(b.account_name, "ja");
                    case "accountCode":
                        return a.account_code.localeCompare(b.account_code);
                    case "role":
                        return (a.permission_level ?? 0) - (b.permission_level ?? 0);
                    case "passwordStatus":
                        return passwordStatusLabel(passwordStatusToKey(a.password_status))
                            .localeCompare(
                                passwordStatusLabel(passwordStatusToKey(b.password_status)),
                                "ja",
                            );
                }
            };
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        accounts,
        accountNameFilter.isFiltering, accountNameFilter.selected,
        accountCodeFilter.isFiltering, accountCodeFilter.selected,
        roleFilter.isFiltering, roleFilter.selected,
        passwordStatusFilter.isFiltering, passwordStatusFilter.selected,
        activeSort,
    ]);

    // ── ダイアログ操作 ──
    const handleCreate = async (values: AccountFormValues) => {
        await createAccount({
            account_name: values.account_name,
            account_code: values.account_code,
            account_role_id: Number(values.account_role_id),
        });
        setDialog({ type: "closed" });
        loadData();
    };

    const handleUpdate = (id: number) => async (values: AccountFormValues) => {
        await updateAccount(id, {
            account_name: values.account_name,
            account_code: values.account_code,
            account_role_id: Number(values.account_role_id),
        });
        setDialog({ type: "closed" });
        loadData();
    };

    const openCreate = () => setDialog({ type: "create" });
    const openEdit = (account: AccountResponse) => setDialog({ type: "edit", account });
    const closeDialog = () => setDialog({ type: "closed" });

    const handleResetClick = (account: AccountResponse) => setResetTarget(account);
    const handleResetSuccess = () => { setResetTarget(null); loadData(); };
    const handleResetCancel = () => setResetTarget(null);

    return (
        <>
            <div className="flex flex-1 flex-col">
                <PageHeader title="アカウント管理" />

                <div className="px-8 pb-6">
                    <div className="w-3/4 mx-auto flex flex-col gap-3 min-w-[820px]">
                    <div className="flex justify-end">
                        <Button variant="outline" size="md" onClick={openCreate}>
                            ＋　新規登録
                        </Button>
                    </div>

                    {error && (
                        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="rounded-lg overflow-hidden border border-gray-200">
                    <div className="max-h-[calc(100svh-260px)] overflow-y-auto">
                    <CommonTable fullWidth>
                        <thead className="sticky top-0 z-20">
                            <tr>
                                {filterColumnList.map((c) => (
                                    <CommonTableHeaderCell
                                        key={c.column}
                                        sortable
                                        sortActive={c.filter.open}
                                        sortDirection={sortFor(c.column)}
                                        filterCount={c.filter.filterCount}
                                        onSort={(anchor) => openOnly(c.column, anchor)}
                                        className={c.widthClass}
                                    >
                                        {c.label}
                                    </CommonTableHeaderCell>
                                ))}
                                <CommonTableHeaderCell className="min-w-[140px]">パスワード初期化</CommonTableHeaderCell>
                                <CommonTableEditHeaderCell label="編集" />
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <CommonTableRow>
                                    <CommonTableCell colSpan={6} className="text-gray-400">
                                        読み込み中…
                                    </CommonTableCell>
                                </CommonTableRow>
                            ) : displayedAccounts.length === 0 ? (
                                <CommonTableRow>
                                    <CommonTableCell colSpan={6} className="text-gray-400">
                                        アカウントがありません
                                    </CommonTableCell>
                                </CommonTableRow>
                            ) : (
                                displayedAccounts.map((account) => (
                                    <CommonTableRow key={account.id}>
                                        <CommonTableCell>{account.account_name}</CommonTableCell>
                                        <CommonTableCell>{account.account_code}</CommonTableCell>
                                        <CommonTableCell>{account.permission_level ?? EMPTY}</CommonTableCell>
                                        <CommonTableCell>{formatPasswordStatus(account.password_status)}</CommonTableCell>
                                        <CommonTableCell>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleResetClick(account)}
                                            >
                                                初期化
                                            </Button>
                                        </CommonTableCell>
                                        <CommonTableEditCell onClick={() => openEdit(account)} />
                                    </CommonTableRow>
                                ))
                            )}
                        </tbody>
                    </CommonTable>
                    </div>
                    </div>
                    </div>
                    </div>
                </div>
            </div>

            {/* 列ごとのフィルター/ソートポップアップ */}
            {filterColumnList.map((c) => (
                <ColumnFilterPopup
                    key={c.column}
                    isOpen={c.filter.open}
                    onClose={() => c.filter.setOpen(false)}
                    anchorRef={c.filter.anchorRef}
                    sortDirection={sortFor(c.column)}
                    onSortChange={handleSortChange(c.column)}
                    options={c.filter.options}
                    selectedValues={c.filter.selected}
                    onSelectedValuesChange={c.filter.setSelected}
                />
            ))}

            {resetTarget && (
                <PasswordResetDialog
                    account={resetTarget}
                    onSuccess={handleResetSuccess}
                    onCancel={handleResetCancel}
                />
            )}

            {dialog.type === "create" && (
                <AccountFormDialog
                    mode="create"
                    initialValues={EMPTY_FORM}
                    roleOptions={roleOptions}
                    onSubmit={handleCreate}
                    onCancel={closeDialog}
                />
            )}
            {dialog.type === "edit" && (
                <AccountFormDialog
                    mode="edit"
                    initialValues={{
                        account_name: dialog.account.account_name,
                        account_code: dialog.account.account_code,
                        account_role_id: String(dialog.account.account_role_id),
                    }}
                    roleOptions={roleOptions}
                    onSubmit={handleUpdate(dialog.account.id)}
                    onCancel={closeDialog}
                />
            )}
        </>
    );
};

export default AccountPage;
